import { ComponentMessageItem } from '../UNSAFE_ComponentMessage';
import { sizeToCSS } from '../utils/UNSAFE_size';
import { trim } from '../utils/UNSAFE_stringUtils';
import { BundleType } from '../resources/nls/bundle';
import { FilePicker } from './FilePicker';
import { JSX, ComponentProps } from 'preact';

type FilePickerProps = ComponentProps<typeof FilePicker>;

/**
 * Returns the aria label
 */
type AriaLabelProps = {
  ariaLabel: FilePickerProps['aria-label'];
  isDisabled: FilePickerProps['isDisabled'];
  translations: BundleType;
  primaryText?: FilePickerProps['primaryText'];
  secondaryText?: FilePickerProps['secondaryText'];
  selectionMode?: FilePickerProps['selectionMode'];
};
export const getAriaLabel = ({
  ariaLabel,
  isDisabled,
  translations,
  primaryText,
  secondaryText,
  selectionMode
}: AriaLabelProps) => {
  return ariaLabel
    ? ariaLabel
    : `${isDisabled ? '' : translations.filePicker_addFiles() + '. '}${getPrimaryText(
        primaryText,
        translations
      )}. ${getSecondaryText(secondaryText, translations, selectionMode)}`;
};

/**
 * Returns the styles of the file picker
 */
export const getStyles = (width: FilePickerProps['width'], height: FilePickerProps['height']) => {
  const dimStyles: JSX.CSSProperties = {
    width: width ? sizeToCSS(width) : undefined,
    height: height ? sizeToCSS(height) : undefined
  };
  return dimStyles;
};

/**
 * Returns the primary text
 */
export const getPrimaryText = (
  primaryText: FilePickerProps['primaryText'],
  translations: BundleType
) => {
  return primaryText ? primaryText : translations.filePicker_dropzonePrimaryText();
};

/**
 * Returns the secondary text text
 */
export const getSecondaryText = (
  secondaryText: FilePickerProps['secondaryText'],
  translations: BundleType,
  selectionMode: FilePickerProps['selectionMode']
) => {
  const defaultSecondaryText =
    selectionMode === 'multiple'
      ? translations.filePicker_dropzoneSecondaryTextMultiple()
      : translations.filePicker_dropzoneSecondaryText();
  return secondaryText ? secondaryText : defaultSecondaryText;
};

/**
 * Takes in a list of files and returns a list of accepted files and
 * a list of rejected files based on file type
 */
export const validateTypes = (
  files: FileList,
  accept: FilePickerProps['accept'],
  translations: BundleType
) => {
  const accepted = [];
  const rejected = [];
  let file: File;
  let type: string;
  if (files) {
    for (let i = 0; i < files.length; i++) {
      file = files[i];
      const name = file.name;
      type = translations.filePicker_unknownFileTypeUploadError();
      if (name) {
        const nameSplit = name.split('.');
        type = nameSplit.length > 1 ? '.' + nameSplit.pop() : type;
      }
      type = file.type ? file.type : type;
      // If type isn't already in one of the lists, add it
      if (accepted.indexOf(type) === -1 && rejected.indexOf(type) === -1) {
        if (_acceptFile(file, accept)) {
          accepted.push(type);
        } else {
          rejected.push(type);
        }
      }
    }
  }
  return { accepted, rejected };
};

/**
 * Creates a file list
 */
export const createFileList = (origList: FileList | DataTransferItemList) => {
  const descriptor: Record<string | number, PropertyDescriptor> = {
    length: { value: origList.length },
    item: {
      value(index: number): File {
        return (this as unknown as FileList)[index];
      }
    }
  };
  for (let i = 0; i < origList.length; i++) {
    descriptor[i] = { value: origList[i], enumerable: true };
  }

  return Object.create(FileList.prototype, descriptor);
};

/**
 * Returns false if selected multiple files when in single file selection mode
 */
export const validateSelectionMode = (
  files: FileList,
  selectionMode: FilePickerProps['selectionMode']
) => {
  return selectionMode !== 'single' || files.length === 1;
};

/**
 * Returns the correct message if mime type validation has failed
 */
export const getMimeTypeValidationMessages = (
  rejected: Array<string>,
  translations: BundleType
): ComponentMessageItem[] => {
  const messages: ComponentMessageItem[] = [];
  if (rejected.length === 1) {
    messages.push({
      severity: 'error',
      summary: translations.filePicker_singleTypeUploadError({
        fileType: rejected[0]
      })
    });
  } else {
    messages.push({
      severity: 'error',
      summary: translations.filePicker_multipleFileTypeUploadError({
        fileTypes: rejected.join(translations.plural_separator())
      })
    });
  }
  return messages;
};

/**
 * Returns true if file passes mimetype validation
 */
const _acceptFile = (file: File, accept: FilePickerProps['accept']) => {
  const acceptProp = accept;
  if (!acceptProp || acceptProp.length === 0 || !file) {
    return true;
  }

  let fileAccept;
  for (let i = 0; i < acceptProp.length; i++) {
    fileAccept = trim(acceptProp[i]) as string;
    if (!fileAccept) {
      return true;
    } else if (fileAccept.startsWith('.', 0)) {
      // when dragover, only MIME type is available, file name is undefined
      // to lowerCase is to make the string comparison case insensitive
      if (!file.name || (file.name && file.name.toLowerCase().endsWith(fileAccept.toLowerCase()))) {
        return true;
      }
    } else if (!file.type) {
      return false;
    } else if (fileAccept === 'image/*') {
      if (file.type.startsWith('image/', 0)) {
        return true;
      }
    } else if (fileAccept === 'video/*') {
      if (file.type.startsWith('video/', 0)) {
        return true;
      }
    } else if (fileAccept === 'audio/*') {
      if (file.type.startsWith('audio/', 0)) {
        return true;
      }
    } else if (file.type === fileAccept) {
      return true;
    }
  }
  return false;
};
